import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useUser } from '../context/UserContext';
import { format, parse } from 'date-fns';
import { IoChevronBackCircle, IoChevronForwardCircle } from 'react-icons/io5';
import { LuCalendarDays } from 'react-icons/lu';
import api from '../utils/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// Statuses that get an orange-tinted card background
const ORANGE_BG_STATUSES = new Set([
  'Holiday',
  'CompOff',
  'HalfCompOff',
  'CompOffApply',
]);

// Statuses where the badge should be hidden (orange bg only, no label)
const HIDE_BADGE_STATUSES = new Set([
  'Holiday',
  'CompOffApply',
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseTime(timeStr) {
  try {
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr))
      return format(parse(timeStr, 'HH:mm:ss', new Date()), 'hh:mm a');
    if (/^\d{2}:\d{2}$/.test(timeStr))
      return format(parse(timeStr, 'HH:mm', new Date()), 'hh:mm a');
  } catch {
    return null;
  }
  return null;
}

function formatHours(v) {
  if (!v || isNaN(v)) return null;
  const h = Math.floor(v);
  const m = Math.round((v - h) * 60);
  return (h > 0 ? `${h}h` : '') + (m > 0 ? ` ${m}m` : '');
}

/**
 * Returns true if the date is a Sunday or a 2nd / 4th Saturday of its month.
 * Used to apply the orange "off day" bg on empty cells.
 */
function isWeekendOff(dateObj) {
  const dow = dateObj.getDay(); // 0 = Sun, 6 = Sat

  if (dow === 0) return true; // Every Sunday is off

  if (dow === 6) {
    // Which Saturday of the month is this? (1-indexed)
    // Day 1-7   → 1st Saturday
    // Day 8-14  → 2nd Saturday  ← off
    // Day 15-21 → 3rd Saturday
    // Day 22-28 → 4th Saturday  ← off
    const satIndex = Math.ceil(dateObj.getDate() / 7);
    return satIndex === 2 || satIndex === 4;
  }

  return false;
}

function getStatusProps(status) {
  const s = (status || '').toString().trim();

  const MAP = {
    Present:                { label: 'Present',        text: 'text-green-700',  border: 'border-green-400'  },
    OnLeave:                { label: 'On Leave',        text: 'text-blue-700',   border: 'border-blue-400'   },
    Absent:                 { label: 'Absent',          text: 'text-red-700',    border: 'border-red-300'    },
    HalfDay:                { label: 'Half Day',        text: 'text-orange-700', border: 'border-orange-400' },
    Late:                   { label: 'Late',            text: 'text-yellow-700', border: 'border-yellow-300' },
    EarlyOut:               { label: 'Early Out',       text: 'text-purple-700', border: 'border-purple-400' },
    Weekend:                { label: 'Weekend',         text: 'text-slate-600',  border: 'border-slate-400'  },
    Holiday:                { label: 'Holiday',         text: 'text-sky-700',    border: 'border-sky-700'    },
    CompOff:                { label: 'Present',         text: 'text-sky-700',    border: 'border-sky-700'    },
    HalfCompOff:            { label: 'Half Comp Off',   text: 'text-sky-700',    border: 'border-sky-700'    },
    CompOffApply:           { label: 'Holiday',         text: 'text-sky-700',    border: 'border-sky-700'    },
    OnDuty:                 { label: 'On Duty',         text: 'text-teal-700',   border: 'border-teal-400'   },
    PresentAndOnDuty:       { label: 'Present & OD',    text: 'text-green-700',  border: 'border-green-400'  },
    AbsentAndOnDuty:        { label: 'Half OD',         text: 'text-red-700',    border: 'border-red-400'    },
    EarnedLeave:            { label: 'Earned Leave',    text: 'text-blue-700',   border: 'border-blue-400'   },
    SickLeave:              { label: 'Sick Leave',      text: 'text-blue-700',   border: 'border-blue-400'   },
    CompOffLeave:           { label: 'Comp Off Leave',  text: 'text-blue-700',   border: 'border-blue-400'   },
    PresentAndSickLeave:    { label: 'Half SL',         text: 'text-green-700',  border: 'border-green-400'  },
    PresentAndEarnedLeave:  { label: 'Half EL',         text: 'text-green-700',  border: 'border-green-400'  },
    PresentAndCompOffLeave: { label: 'Half COL',        text: 'text-green-700',  border: 'border-green-400'  },
    AbsentAndSickLeave:     { label: 'Half SL',         text: 'text-red-700',    border: 'border-red-400'    },
    AbsentAndEarnedLeave:   { label: 'Half EL',         text: 'text-red-700',    border: 'border-red-400'    },
    AbsentAndCompOffLeave:  { label: 'Half COL',        text: 'text-red-700',    border: 'border-red-400'    },
    OndutyAndSickLeave:     { label: 'OD & Half SL',    text: 'text-teal-700',   border: 'border-teal-400'   },
    OnDutyAndEarnedLeave:   { label: 'OD & Half EL',    text: 'text-teal-700',   border: 'border-teal-400'   },
    OndutyAndCompOffLeave:  { label: 'OD & Half COL',   text: 'text-teal-700',   border: 'border-teal-400'   },
  };

  if (MAP[s]) return MAP[s];

  const lower = s.toLowerCase();
  if (lower.includes('sick'))   return { label: 'Sick Leave',   text: 'text-blue-700',   border: 'border-blue-400'   };
  if (lower.includes('earned')) return { label: 'Earned Leave', text: 'text-blue-700',   border: 'border-blue-400'   };
  if (lower.includes('comp'))   return { label: 'Comp Off',     text: 'text-indigo-700', border: 'border-indigo-400' };
  if (lower.includes('duty'))   return { label: 'On Duty',      text: 'text-teal-700',   border: 'border-teal-400'   };

  return { label: s || 'Unknown', text: 'text-gray-700', border: 'border-gray-300' };
}

// ─── Day Card (data present) ──────────────────────────────────────────────────

function DayCard({ row }) {
  const { label, text, border } = getStatusProps(row.status);
  const inTime  = row.punchInTime  ? parseTime(row.punchInTime)  : null;
  const outTime = row.punchOutTime ? parseTime(row.punchOutTime) : null;
  const hrs     = formatHours(row.totalHoursWorked);
  const ot      = row.overtimeHours > 0 ? formatHours(row.overtimeHours) : null;
  const dateObj = new Date(row.date);

  const rawStatus   = (row.status || '').toString().trim();
  const useOrangeBg = ORANGE_BG_STATUSES.has(rawStatus);
  const hideBadge   = HIDE_BADGE_STATUSES.has(rawStatus);
  const cardBg      = useOrangeBg
    ? 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200'
    : 'bg-gradient-to-r from-gray-100 to-gray-200 border-gray-200';

  return (
    <div className={`flex flex-col shadow rounded-xl border overflow-hidden ${cardBg}`}>
      <div className="p-3 flex flex-col h-full">

        {/* Date + status badge */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-col">
            <span className="text-xl font-bold text-slate-800 leading-none">
              {format(dateObj, 'dd')}
            </span>
            <span className="text-[10px] uppercase font-bold text-slate-400 mt-0.5">
              {format(dateObj, 'EEE')}
            </span>
          </div>
          {!hideBadge && (
            <span className={`px-2 py-1 rounded-full text-[10px] font-bold tracking-wide border bg-gray-50 shadow-md ${text}`}>
              {label}
            </span>
          )}
        </div>

        {/* In / Out */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span className="text-[10px] font-medium text-slate-400 uppercase">In</span>
            </div>
            <span className="text-sm font-bold text-slate-700 font-mono tracking-tight">
              {inTime || '--:--'}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[10px] font-medium text-slate-400 uppercase">Out</span>
              <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            </div>
            <span className="text-sm font-bold text-slate-700 font-mono tracking-tight">
              {outTime || '--:--'}
            </span>
          </div>
        </div>

        {/* Hours + OT */}
        <div className={`mt-auto pt-2 border-t-2 ${border} flex items-center justify-between min-h-[28px]`}>
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-semibold text-slate-600">{hrs || '-'}</span>
          </div>
          {ot && (
            <div className="flex items-center gap-1 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
              <svg className="w-3 h-3 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-[10px] font-bold text-amber-600 tabular-nums">{ot}</span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Empty Cell (no data for that date) ──────────────────────────────────────

function EmptyCell({ day, year, month, holidays = [] }) {
  const dateObj = new Date(year, month, day);

  const holiday = holidays.find(h => {
    const hDate = new Date(h.holidayDate);
    return (
      hDate.getFullYear() === year &&
      hDate.getMonth()    === month &&
      hDate.getDate()     === day
    );
  });

  const isHoliday    = !!holiday;
  const isWeekendDay = isWeekendOff(dateObj);

  // Orange bg for: holidays, Sundays, 2nd & 4th Saturdays
  const useOrangeBg = isHoliday || isWeekendDay;
  const cardBg = useOrangeBg
    ? 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200'
    : 'bg-gradient-to-r from-gray-100 to-gray-200 border-gray-200';

  return (
    <div className={`flex flex-col shadow rounded-xl border overflow-hidden ${cardBg}`}>
      <div className="p-3 flex flex-col h-full">

        {/* Date */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-col">
            <span className="text-xl font-bold text-slate-800 leading-none">
              {String(day).padStart(2, '0')}
            </span>
            <span className="text-[10px] uppercase font-bold text-slate-400 mt-0.5">
              {format(dateObj, 'EEE')}
            </span>
          </div>
          <div className="w-16 h-6" />
        </div>

        {/* In / Out */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-300" />
              <span className="text-[10px] font-medium text-slate-400 uppercase">In</span>
            </div>
            <span className="text-sm font-bold text-slate-500 font-mono tracking-tight">--:--</span>
          </div>

          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[10px] font-medium text-slate-400 uppercase">Out</span>
              <div className="w-1.5 h-1.5 rounded-full bg-rose-300" />
            </div>
            <span className="text-sm font-bold text-slate-500 font-mono tracking-tight">--:--</span>
          </div>
        </div>

        <div className="mt-auto pt-2 border-t-2 border-slate-300 min-h-[28px]">
          <span className="text-xs font-semibold text-slate-500">-</span>
        </div>
      </div>
    </div>
  );
}

// ─── Calendar Grid ────────────────────────────────────────────────────────────

function CalendarGrid({ year, month, dataMap, holidays }) {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <>
      {/* Weekday labels — always 7 columns */}
      <div className="grid grid-cols-7 mb-3">
        {WEEKDAYS.map(wd => (
          <div key={wd} className="text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider py-1">
            {wd}
          </div>
        ))}
      </div>

      {/* Day grid — 2 cols mobile → 4 cols sm → 7 cols lg */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {cells.map((day, idx) =>
          day === null
            ? <div key={`blank-${idx}`} className="rounded-xl" />
            : dataMap[day]
              ? <DayCard key={day} row={dataMap[day]} />
              : <EmptyCell
                  key={day}
                  day={day}
                  year={year}
                  month={month}
                  holidays={holidays}
                />
        )}
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function Attendance() {
  const { currentUser, loading } = useUser();
  const [attendance,    setAttendance]    = useState([]);
  const [isLoading,     setIsLoading]     = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [holidays,      setHolidays]      = useState([]);

  const year  = selectedMonth.getFullYear();
  const month = selectedMonth.getMonth();

  const dataMap = useMemo(() => {
    const map = {};
    attendance.forEach(row => {
      map[new Date(row.date).getDate()] = row;
    });
    return map;
  }, [attendance]);

  const fetchAttendance = async () => {
    if (!currentUser?.id) return;
    setIsLoading(true);
    try {
      const response = await api.get(`/api/daily-attendance/${currentUser.id}?size=500`);
      const allData  = response.data.content || [];
      const filtered = allData.filter(row => {
        const d = new Date(row.date);
        return d.getFullYear() === year && d.getMonth() === month;
      });
      setAttendance(filtered);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch attendance data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await api.get(`/api/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  useEffect(() => {
    if (!loading && currentUser?.id) fetchAttendance();
    // eslint-disable-next-line
  }, [currentUser?.id, loading, selectedMonth]);

  useEffect(() => { fetchHolidays(); }, []);

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 mb-20 sm:mb-auto w-full mx-auto">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 bg-blue-100 rounded-2xl p-6 border border-slate-300">
        <div className="flex items-center gap-4 text-xl xs:text-2xl font-bold truncate">
          <div className="p-2 rounded-xl bg-slate-700 text-slate-100">
            <LuCalendarDays className="text-2xl" />
          </div>
          <h2 className="text-xl xs:text-2xl font-bold">Attendance</h2>
        </div>

        <div className="flex gap-4 items-center">
          <button
            className="text-slate-500 active:text-slate-800 transition-colors"
            onClick={() => setSelectedMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          >
            <IoChevronBackCircle className="text-4xl" />
          </button>
          <span className="font-bold text-slate-800 text-lg sm:text-xl select-none min-w-[140px] text-center">
            {MONTHS[month]} {year}
          </span>
          <button
            className="text-slate-500 active:text-slate-800 transition-colors"
            onClick={() => setSelectedMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          >
            <IoChevronForwardCircle className="text-4xl" />
          </button>
        </div>
      </div>

      {/* ── Calendar body ── */}
      <motion.div
        className="bg-white rounded-2xl p-4 sm:p-8 border border-slate-200"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${year}-${month}`}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
            >
              <CalendarGrid year={year} month={month} dataMap={dataMap} holidays={holidays} />
            </motion.div>
          </AnimatePresence>
        )}
      </motion.div>
    </div>
  );
}

export default Attendance;